import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { submitFranchise, formatApiError } from "../../lib/api";
import { CONTACT } from "../../data";

export const Franchise = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    city: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.city) {
      toast.error("Please add your name, phone and preferred city.");
      return;
    }
    setLoading(true);
    try {
      await submitFranchise(form);
      const msg = `*New Franchise Enquiry — CARFIX AUTO*%0A%0AName: ${form.name}%0APhone: ${form.phone}%0AEmail: ${form.email || "-"}%0ACity: ${form.city}%0AMessage: ${form.message || "-"}`;
      window.open(`https://wa.me/${CONTACT.whatsapp}?text=${msg}`, "_blank");
      toast.success("Franchise enquiry sent — our expansion team will reach out soon.");
      setForm({ name: "", phone: "", email: "", city: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const field =
    "w-full bg-transparent border-b border-white/20 py-3 font-mono text-sm text-white placeholder:text-white/40 focus:border-[var(--volt)] focus:outline-none transition-colors duration-300";

  return (
    <section
      id="franchise"
      data-testid="franchise"
      className="border-y border-white/10 bg-[#0a0a0a] py-24 md:py-36"
    >
      <div className="mx-auto grid max-w-[1500px] grid-cols-1 gap-16 px-6 md:px-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-[var(--volt)]">
            / Partner With Us
          </span>
          <h2 className="mt-5 font-display text-4xl font-bold uppercase leading-[1.02] tracking-tight text-white md:text-6xl">
            Bring CARFIX
            <br />
            <span className="text-stroke">to your city.</span>
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-white/60 md:text-lg">
            We&apos;re expanding towards 250+ German-equipped centres across India.
            Share your details and our expansion team will get in touch to walk
            you through the CARFIX franchise opportunity.
          </p>

          <ul className="mt-10 space-y-4">
            {[
              "Proven German-equipment operating playbook",
              "Full setup, training & branding support",
              "Access to a growing pan-India network",
            ].map((p) => (
              <li
                key={p}
                className="flex items-start gap-3 font-mono text-sm text-white/70"
              >
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--volt)]" />
                {p}
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-7">
          <form
            data-testid="franchise-form"
            onSubmit={submit}
            className="grid grid-cols-1 gap-8 sm:grid-cols-2"
          >
            <input
              data-testid="franchise-name"
              className={field}
              placeholder="Full name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <input
              data-testid="franchise-phone"
              className={field}
              placeholder="Phone number"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
            <input
              data-testid="franchise-email"
              className={field}
              placeholder="Email address"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <input
              data-testid="franchise-city"
              className={field}
              placeholder="Preferred city / location"
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
            />
            <textarea
              data-testid="franchise-message"
              className={`${field} sm:col-span-2 resize-none`}
              rows={3}
              placeholder="Tell us a little about yourself (optional)"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
            />
            <motion.button
              data-testid="franchise-submit"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={loading}
              className="group mt-2 flex items-center justify-center gap-3 rounded-full bg-[var(--volt)] px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-black disabled:opacity-60 sm:col-span-2"
            >
              {loading ? "Sending…" : "Submit Franchise Enquiry"}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </motion.button>
          </form>
        </div>
      </div>
    </section>
  );
};
